import { useCallback, useEffect, useRef, useState } from "react";
import { apiIsConfigured, fetchJobs } from "../lib/api";
import type { DataMode, Job } from "../types/job";

interface JobsState {
  jobs: Job[];
  status: "loading" | "ready" | "error";
  error: string | null;
  mode: DataMode;
  updatedAt: Date | null;
}
export function useJobs() {
  const [state, setState] = useState<JobsState>({
    jobs: [],
    status: "loading",
    error: null,
    mode: apiIsConfigured() ? "live" : "mock",
    updatedAt: null,
  });
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((current) => ({ ...current, status: "loading", error: null }));
    try {
      const result = await fetchJobs(controller.signal);
      if (controller.signal.aborted) return;
      setState({
        jobs: result.jobs,
        status: "ready",
        error: null,
        mode: result.mode,
        updatedAt: new Date(),
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      setState((current) => ({
        ...current,
        status: "error",
        error: error instanceof Error ? error.message : "Unable to reach Job Lobster.",
      }));
    }
  }, []);

  useEffect(() => {
    void load();
    return () => controllerRef.current?.abort();
  }, [load]);

  return { ...state, reload: load };
}
